/**
 * **Merkt, ob die Seite älter ist als der Server** (7.2 Abschnitt 46, R1441).
 *
 * Am 2026-10-07 speicherte der Auftraggeber im Konzernhandbuch drei Konten
 * über eine Seite, die eine ältere Fassung der Oberfläche war, und bekam
 * „gespeichert" — angekommen war keines. Seither nennt der Server bei jeder
 * Antwort seine Fassung (den Stand des Quelltexts, aus dem er gebaut ist),
 * und die Seite vergleicht sie mit ihrer eigenen. Weicht sie ab, steht oben
 * der Hinweis zum Neuladen, und Speichern ist gesperrt.
 *
 * **Die eigene Fassung:** In der Auslieferung schreibt der Bau sie in die
 * Seite. Am Entwicklungsserver fragt die Seite ihn, aus welchem Stand er
 * gerade liefert — dort lädt die Seite geänderten Quelltext von selbst nach,
 * und eine eingebaute Fassung wäre nach dem ersten Wechsel des Stands falsch.
 * Lässt sich die eigene Fassung nicht ermitteln, wird nicht verglichen.
 */

import { rufe } from './Stoerungsstelle'

declare const __COKO_FASSUNG__: string | null | undefined

/** Das Ereignis, mit dem die Seite meldet, dass sie veraltet ist. */
export const VERALTET = 'coko:veraltet'

/** Wo der Entwicklungsserver seine Fassung nennt. */
export const ADRESSE = '/__coko_fassung'

/** Der Kopf, in dem der Server seine Fassung nennt. */
export const KOPF = 'Coko-Fassung'

let eigene: string | null =
  !import.meta.env.DEV && typeof __COKO_FASSUNG__ === 'string' ? __COKO_FASSUNG__ : null
let veraltet = false

/** Ob die Seite älter ist als der Server — dann wird nicht gespeichert. */
export function istVeraltet(): boolean {
  return veraltet
}

/** Fragt den Entwicklungsserver nach dem Stand, aus dem er gerade liefert. */
async function holeEigeneFassung(): Promise<string | null> {
  try {
    const antwort = await rufe(ADRESSE)
    return antwort.ok ? (((await antwort.json()) as { fassung?: string | null }).fassung ?? null) : null
  } catch {
    return null
  }
}

/**
 * Vergleicht die Fassung des Servers mit der eigenen. Steht keine da, wird
 * nicht verglichen; weicht sie ab, wird vorher noch einmal nachgefragt —
 * am Entwicklungsserver kann der Stand gewechselt haben, seit die Seite
 * zuletzt gefragt hat.
 */
export async function vergleiche(
  server: string | null | undefined,
  holeEigene: () => Promise<string | null> = holeEigeneFassung,
): Promise<void> {
  if (!server || veraltet || eigene === server) {
    return
  }

  if (import.meta.env.DEV || eigene === null) {
    eigene = await holeEigene()
    if (eigene === null || eigene === server) {
      return
    }
  }

  veraltet = true
  window.dispatchEvent(new CustomEvent(VERALTET))
}

/** Nur für die Prüffälle: der Anfangszustand. */
export function zuruecksetzen(): void {
  eigene = null
  veraltet = false
}
